import { Injectable, ElementRef } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CanvasService {
  private strokeHistory: any[][] = [];

  initializeCanvas(canvasElement: ElementRef<HTMLCanvasElement>): CanvasRenderingContext2D | undefined {
    if (!canvasElement) {
      console.error(`Canvas not found`);
      return undefined;
    }

    const ctx = canvasElement.nativeElement.getContext('2d', { willReadFrequently: true });
    if (!ctx) return undefined;

    ctx.canvas.width = canvasElement.nativeElement.clientWidth * window.devicePixelRatio;
    ctx.canvas.height = canvasElement.nativeElement.clientHeight * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    return ctx;
  }

  clearCanvas(canvasContext?: CanvasRenderingContext2D): void {
    if (canvasContext) {
      canvasContext.clearRect(0, 0, canvasContext.canvas.width, canvasContext.canvas.height);
    }
  }

  onMouseDown(event: MouseEvent, scene: any): void {
    scene.isDrawing = true;
    scene.lastX = event.offsetX;
    scene.lastY = event.offsetY;
    scene.points = [];
  }

  onMouseMove(event: MouseEvent, scene: any): void {
    if (!scene.isDrawing || !scene.canvasContext) return;

    const x = event.offsetX;
    const y = event.offsetY;

    scene.canvasContext.lineWidth = scene.lineWidth;
    scene.canvasContext.lineCap = 'round';
    scene.canvasContext.lineJoin = 'round';

    scene.canvasContext.beginPath();
    scene.canvasContext.moveTo(scene.lastX, scene.lastY);
    scene.canvasContext.lineTo(x, y);
    scene.canvasContext.stroke();

    scene.lastX = x;
    scene.lastY = y;
    scene.points.push({ x, y, lineWidth: scene.lineWidth });
  }

  onMouseUp(scene: any): void {
    scene.isDrawing = false;
    if (scene.points?.length > 0) {
      this.strokeHistory.push([...scene.points]);
    }
    scene.points = [];
  }

  onTouchStart(event: TouchEvent, scene: any): void {
    event.preventDefault();
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
      let pressure = touch.force || 0.1;

      scene.isDrawing = true;
      scene.lastX = touch.clientX - rect.left;
      scene.lastY = touch.clientY - rect.top;
      scene.lineWidth = Math.log(pressure + 1) * 40;
      scene.points = [];
    }
  }

  onTouchMove(event: TouchEvent, scene: any): void {
    event.preventDefault();
    if (!scene.isDrawing || !scene.canvasContext || event.touches.length === 0) return;

    const touch = event.touches[0];
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    let pressure = touch.force || 0.1;

    scene.lineWidth = (Math.log(pressure + 1) * 40 * 0.2 + scene.lineWidth * 0.8);

    scene.canvasContext.lineWidth = scene.lineWidth;
    scene.canvasContext.lineCap = 'round';
    scene.canvasContext.lineJoin = 'round';

    scene.canvasContext.beginPath();
    scene.canvasContext.moveTo(scene.lastX, scene.lastY);
    scene.canvasContext.lineTo(x, y);
    scene.canvasContext.stroke();

    scene.lastX = x;
    scene.lastY = y;
    scene.points.push({ x, y, lineWidth: scene.lineWidth });
  }

  onTouchEnd(scene: any): void {
    scene.isDrawing = false;
    if (scene.points?.length > 0) {
      this.strokeHistory.push([...scene.points]);
    }
    scene.points = [];
  }

  undoDraw(scene: any): void {
    if (this.strokeHistory.length === 0) return;
    this.strokeHistory.pop();
    scene.canvasContext.clearRect(0, 0, scene.canvasContext.canvas.width, scene.canvasContext.canvas.height);

    this.strokeHistory.forEach(stroke => {
      let strokePath = [];
      stroke.forEach((point: any) => {
        strokePath.push(point);
        this.drawOnCanvas(scene.canvasContext, strokePath);
      });
    });
  }

  private drawOnCanvas(context: CanvasRenderingContext2D, stroke: any[], color: string = 'black'): void {
    context.strokeStyle = color;
    context.lineCap = 'round';
    context.lineJoin = 'round';

    const l = stroke.length - 1;
    if (stroke.length >= 3) {
      const xc = (stroke[l].x + stroke[l - 1].x) / 2;
      const yc = (stroke[l].y + stroke[l - 1].y) / 2;
      context.lineWidth = stroke[l - 1].lineWidth;
      context.quadraticCurveTo(stroke[l - 1].x, stroke[l - 1].y, xc, yc);
      context.stroke();
      context.beginPath();
      context.moveTo(xc, yc);
    } else {
      const point = stroke[l];
      context.lineWidth = point.lineWidth;
      context.strokeStyle = color;
      context.beginPath();
      context.moveTo(point.x, point.y);
      context.stroke();
    }
  }
}
